import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { env, isAuthEnabled } from '@/config/env';
import { getMsalInstance } from '@/api/msalInstance';
import { apiScopes } from '@/config/auth';

// In dev mode, Vite's proxy handles /api → localhost:5003
// In production, we use the full apiUrl base
const baseUrl = env.isDev ? '' : env.apiUrl;

// Cache the API token to avoid repeated acquireTokenSilent calls.
// API scopes are requested during login so MSAL usually has them cached,
// but we still cache here to avoid any redundant iframe calls.
let cachedApiToken: string | null = null;
let cachedApiTokenExpiry = 0;
let inflightApiToken: Promise<string | null> | null = null;

async function getApiToken(): Promise<string | null> {
  if (!isAuthEnabled) return null;

  // Return cached token if still valid (with 5-minute buffer)
  if (cachedApiToken && Date.now() < cachedApiTokenExpiry) {
    return cachedApiToken;
  }

  // Deduplicate concurrent requests
  if (inflightApiToken) return inflightApiToken;

  inflightApiToken = acquireApiToken().finally(() => {
    inflightApiToken = null;
  });
  return inflightApiToken;
}

async function acquireApiToken(): Promise<string | null> {
  const msal = getMsalInstance();
  if (!msal) return null;

  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    const response = await msal.acquireTokenSilent({
      scopes: apiScopes,
      account: accounts[0],
    });
    cachedApiToken = response.accessToken;
    cachedApiTokenExpiry = response.expiresOn
      ? response.expiresOn.getTime() - 5 * 60 * 1000
      : Date.now() + 50 * 60 * 1000;
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({
        scopes: apiScopes,
        account: accounts[0],
      });
    }
    throw new Error('Authentication failed. Please refresh and try again.');
  }
}

async function getHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  const token = await getApiToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export const apiClient = {
  async get<T>(
    path: string,
    params?: Record<string, string | number | boolean | undefined>,
  ): Promise<T> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          searchParams.set(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    const url = `${baseUrl}${path}${query ? `?${query}` : ''}`;
    const res = await fetch(url, {
      headers: await getHeaders(),
    });
    if (!res.ok) throw new Error(`API error: ${res.status} ${res.statusText}`);
    return res.json() as Promise<T>;
  },

  async post<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: { ...(await getHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`API error: ${res.status} ${res.statusText}`);
    return res.json() as Promise<T>;
  },

  async put<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(`${baseUrl}${path}`, {
      method: 'PUT',
      headers: { ...(await getHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`API error: ${res.status} ${res.statusText}`);
    return res.json() as Promise<T>;
  },

  async delete(path: string): Promise<void> {
    const res = await fetch(`${baseUrl}${path}`, {
      method: 'DELETE',
      headers: await getHeaders(),
    });
    if (!res.ok) throw new Error(`API error: ${res.status} ${res.statusText}`);
  },
};
